import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Switch,
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MRT_Column, MRT_ColumnOrderState, MRT_TableInstance } from 'material-react-table';

interface Props {
  table: MRT_TableInstance<any>;
}
interface SortableColumnMenuItemProps {
  column: MRT_Column<any>;
  isDragDisabled: boolean;
  onPinLeft: (column: MRT_Column<any>) => void;
  onPinRight: (column: MRT_Column<any>) => void;
  onToggleVisibility: (column: MRT_Column<any>, isVisible: boolean) => void;
}

const SortableColumnMenuItem = ({
  column,
  isDragDisabled,
  onPinLeft,
  onPinRight,
  onToggleVisibility
}: SortableColumnMenuItemProps) => {
  const id = column.id;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
  };

  const headerText = typeof column.columnDef.header === 'string'
    ? column.columnDef.header
    : column.id;

  const isPinnedLeft = column.getIsPinned() === 'left';
  const isPinnedRight = column.getIsPinned() === 'right';
  const isVisible = column.getIsVisible();
  const canHide = column.getCanHide();
  const canPin = column.getCanPin();
  const canOrder = column.columnDef.enableColumnDragging !== false;


  return (
    <MenuItem
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 0.5,
        minHeight: '36px'
      }}
    >
      {/* Drag Indicator */}
      {canOrder && !isDragDisabled ? (
        <Tooltip title="Drag to reorder" arrow placement="top">
          <Box {...attributes} {...listeners} sx={{ display: 'flex', cursor: 'grab', color: 'text.secondary', mr: 0.5 }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ width: 24, mr: 0.5 }} />
      )}

      {/* Pin Left Button */}
      {canPin ? (
        <Tooltip title={isPinnedLeft ? "Unpin Column" : "Pin to Left"} arrow placement="top">
          <IconButton
            size="small"
            onClick={() => onPinLeft(column)}
            color={isPinnedLeft ? "primary" : "default"}
            sx={{ p: 0.25 }}
          >
            <PushPinIcon
              fontSize="small"
              sx={{
                transform: 'rotate(-90deg)',
                opacity: isPinnedLeft ? 1 : 0.4,
                fontSize: '0.85rem'
              }}
            />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ width: 24 }} />
      )}

      {/* Pin Right Button */}
      {canPin ? (
        <Tooltip title={isPinnedRight ? "Unpin Column" : "Pin to Right"} arrow placement="top">
          <IconButton
            size="small"
            onClick={() => onPinRight(column)}
            color={isPinnedRight ? "primary" : "default"}
            sx={{ p: 0.25 }}
          >
            <PushPinIcon
              fontSize="small"
              sx={{
                transform: 'rotate(90deg)',
                opacity: isPinnedRight ? 1 : 0.4,
                fontSize: '0.85rem'
              }}
            />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ width: 24 }} />
      )}

      {/* Visibility Toggle Switch */}
      {canHide ? (
        <Tooltip title={isVisible ? "Hide Column" : "Show Column"} arrow placement="top">
          <Switch
            size="small"
            checked={isVisible}
            onChange={(e) => onToggleVisibility(column, e.target.checked)}
            sx={{ mx: 0.5 }}
          />
        </Tooltip>
      ) : (
        <Box sx={{ width: 34, mx: 0.5 }} />
      )}

      {/* Column Label */}
      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.8rem', color: 'text.primary' }}>
        {headerText}
      </Typography>
    </MenuItem>
  );
};

export const TableColumnVisibilityMenu: React.FC<Props> = ({ table }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [searchQuery, setSearchQuery] = useState('');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  // Get all leaf columns that are allowed to be in the hide/show menu
  const columns = table.getAllLeafColumns().filter(
    col => col.columnDef.visibleInShowHideMenu !== false
  );

  // Filter columns based on search query
  const filteredColumns = columns.filter((col: MRT_Column<any>) => {
    const headerText = typeof col.columnDef.header === 'string'
      ? col.columnDef.header
      : col.id;
    return headerText.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Reorder columns using columnOrder state
  const columnOrder: MRT_ColumnOrderState = table.getState().columnOrder;

  // Sort columns in the menu based on the current column order state
  const sortedColumns = [...filteredColumns].sort((a: MRT_Column<any>, b: MRT_Column<any>) => {
    const aIndex = columnOrder.indexOf(a.id);
    const bIndex = columnOrder.indexOf(b.id);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  const handleHideAll = (): void => {
    columns.forEach(col => {
      if (col.getCanHide()) col.toggleVisibility(false);
    });
  };

  const handleShowAll = (): void => {
    columns.forEach(col => {
      if (col.getCanHide()) col.toggleVisibility(true);
    });
  };

  const handleResetOrder = (): void => {
    table.resetColumnOrder();
  };

  const handleUnpinAll = (): void => {
    table.setColumnPinning({ left: [], right: [] });
  };

  const handlePinLeft = (column: MRT_Column<any>): void => {
    const isPinned = column.getIsPinned() === 'left';
    column.pin(isPinned ? false : 'left');
  };

  const handlePinRight = (column: MRT_Column<any>): void => {
    const isPinned = column.getIsPinned() === 'right';
    column.pin(isPinned ? false : 'right');
  };

  const handleToggleVisibility = (column: MRT_Column<any>, isVisible: boolean): void => {
    column.toggleVisibility(isVisible);
  };

  const pinningState = table.getState().columnPinning;
  const hasPinned = (pinningState.left && pinningState.left.length > 0) || (pinningState.right && pinningState.right.length > 0);

  const hideableColumns = columns.filter(col => col.getCanHide());
  const allHidden = hideableColumns.every(col => !col.getIsVisible());
  const allVisible = hideableColumns.every(col => col.getIsVisible());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
        table.setColumnOrder(newOrder);
      }
    }
  };

  return (
    <>
      <Tooltip title="Show/Hide columns">
        <IconButton onClick={handleClick} size="small">
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 320, p: 0 } } }}
      >
        <Box sx={{ p: 1, display: 'flex', gap: 0.5, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Button size="small" onClick={handleHideAll} disabled={allHidden} sx={{ fontSize: '0.7rem' }}>Hide All</Button>
          <Button size="small" onClick={handleResetOrder} sx={{ fontSize: '0.7rem' }}>Reset Order</Button>
          <Button size="small" onClick={handleUnpinAll} disabled={!hasPinned} sx={{ fontSize: '0.7rem' }}>Unpin All</Button>
          <Button size="small" onClick={handleShowAll} disabled={allVisible} sx={{ fontSize: '0.7rem' }}>Show All</Button>
        </Box>

        <Divider />

        {/* Search Bar */}
        <TextField
          size="small"
          placeholder="Search columns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            mx: 1.5,
            my: 1,
            width: 'calc(100% - 24px)',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <Divider />

        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedColumns.map(col => col.id)} strategy={verticalListSortingStrategy}>
              {sortedColumns.map(column => (
                <SortableColumnMenuItem
                  key={column.id}
                  column={column}
                  isDragDisabled={!!searchQuery}
                  onPinLeft={handlePinLeft}
                  onPinRight={handlePinRight}
                  onToggleVisibility={handleToggleVisibility}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      </Menu>
    </>
  );
};